
import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';

const JobApplicationForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    message: '',
    cv: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [positions, setPositions] = useState([]);
  const fileInputRef = useRef(null);
  const { t } = useTranslation('jobs');

  // Load enabled positions from backend
  useEffect(() => {
    const loadPositions = async () => {
      try {
        const response = await fetch('/backend/api/job_applications.php?path=positions');
        const contentType = response.headers.get('content-type');

        if (response.ok && contentType && contentType.includes('application/json')) {
          const data = await response.json();
          if (data.success) {
            setPositions(data.data.map(pos => ({
              value: pos.position_key,
              label: pos.title
            })));
          }
        } else {
          // Ako server vrati PHP/HTML ili ne-JSON odgovor, čuvamo default vrednosti
          return;
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('Error loading positions (using defaults):', error);
        }
        // Fallback to static positions
        setPositions([
          { value: 'prodavac', label: 'Prodavac IT opreme' },
          { value: 'magacioner', label: 'Magacioner' },
          { value: 'vozac', label: 'Vozač' },
          { value: 'serviser', label: 'Serviser IT opreme' },
          { value: 'knjigovodja', label: 'Administrativni radnik / Knjigovođa' },
          { value: 'hr-menadzer', label: 'HR menadzer' }
        ]);
      }
    };

    loadPositions();
  }, []);

  // Listen for job selection event
  useEffect(() => {
    const handleJobSelected = (event) => {
      const selectedJobId = event.detail;
      setFormData(prev => ({
        ...prev,
        position: selectedJobId
      }));
    };

    window.addEventListener('jobSelected', handleJobSelected);

    return () => {
      window.removeEventListener('jobSelected', handleJobSelected);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        alert('Molimo izaberite PDF, DOC ili DOCX fajl.');
        return;
      }
      
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('Fajl je prevelik. Maksimalna velicina je 5MB.');
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        cv: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });

      // Send to PHP backend
      const response = await fetch('/backend/api/job_applications.php?path=submit', {
        method: 'POST',
        body: submitData
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          position: '',
          message: '',
          cv: null
        });
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        throw new Error(data.error || 'Greska pri slanju prijave');
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      const fakeEvent = {
        target: {
          files: [file]
        }
      };
      handleFileChange(fakeEvent);
    }
  };

  return (
    <section 
      id="job-application-form"
      style={{ contain: 'paint' }}
      className="relative py-16 lg:py-24 overflow-hidden">
        
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-secondary opacity-95 pointer-events-none"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 via-transparent to-accent/10 pointer-events-none"></div>
      
      {/* Dark theme overlay for perfect text contrast */}
      <div className="absolute inset-0 hero-dark-overlay opacity-70 [data-theme='dark']:opacity-100 pointer-events-none"></div>
      
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 opacity-10">
          <svg viewBox="0 0 48 48" className="w-full h-full">
            <polygon 
              points="24,2 38,12 38,28 24,38 10,28 10,12" 
              fill="var(--color-accent)"
            />
          </svg>
        </div>
        <div className="absolute bottom-20 right-10 w-24 h-24 opacity-10">
          <svg viewBox="0 0 48 48" className="w-full h-full">
            <polygon 
              points="24,2 38,12 38,28 24,38 10,28 10,12" 
              fill="var(--color-surface)"
            />
          </svg>
        </div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 opacity-5">
          <svg viewBox="0 0 48 48" className="w-full h-full">
            <polygon 
              points="24,2 38,12 38,28 24,38 10,28 10,12" 
              fill="var(--color-primary)"
            />
          </svg>
        </div>
      </div>
      
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-10 w-32 h-32">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <polygon points="50,5 85,25 85,65 50,85 15,65 15,25" fill="var(--color-accent)"/>
          </svg>
        </div>
        <div className="absolute bottom-20 left-10 w-24 h-24">
          <svg viewBox="0 0 100 100" className="w-full h-full" style={{opacity:0.75}}>
            <polygon points="50,5 85,25 85,65 50,85 15,65 15,25" fill="var(--color-surface)"/>
          </svg>
        </div>
      </div>

      <div className="relative container mx-auto px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="font-heading font-bold text-3xl lg:text-4xl xl:text-5xl text-foreground mb-4">
            {t('form.title', 'Prijavi se za posao')}
          </h2>
          <p className="text-text-secondary text-lg lg:text-xl max-w-3xl mx-auto">
            {t('form.description', 'Popuni formu i posalji nam svoje podatke. Kontaktiracu te u najkracem roku!')}
          </p>
        </motion.div>

        <motion.div
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="bg-card rounded-2xl p-8 lg:p-12 shadow-hexagon">
            {submitStatus === 'success' && (
              <motion.div
                className="mb-8 p-4 bg-success/10 border border-success/20 rounded-lg flex items-center space-x-3"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Icon name="CheckCircle" size={24} className="text-success" />
                <div>
                  <h4 className="font-heading font-bold text-success">{t('form.successTitle', 'Prijava je uspešno poslata!')}</h4>
                  <p className="text-success text-sm">{t('form.successDesc', 'Hvala što ste se prijavili. Kontaktiraćemo vas u najkraćem roku.')}</p>
                </div>
              </motion.div>
            )}

            {submitStatus === 'error' && (
              <motion.div
                className="mb-8 p-4 bg-error/10 border border-error/20 rounded-lg flex items-center space-x-3"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Icon name="AlertCircle" size={24} className="text-error" />
                <div>
                  <h4 className="font-heading font-bold text-error">{t('form.errorTitle', 'Greška pri slanju prijave')}</h4>
                  <p className="text-error text-sm">{t('form.errorDesc', 'Molimo pokušajte ponovo ili nas kontaktirajte direktno.')}</p>
                </div>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6" name="job-application" aria-label="Forma za prijavu na posao">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-body font-medium text-foreground mb-2">
                    {t('form.fullName', 'Ime i prezime *')}
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder={t('form.firstName', 'Ime')}
                      required
                      className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder-text-secondary focus:border-accent focus:outline-none transition-colors"
                    />
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder={t('form.lastName', 'Prezime')}
                      required
                      className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder-text-secondary focus:border-accent focus:outline-none transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-body font-medium text-foreground mb-2">
                    {t('form.email', 'Email adresa *')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t('form.emailPlaceholder', 'ime@example.com')}
                    required
                    className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder-text-secondary focus:border-accent focus:outline-none transition-colors"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-body font-medium text-foreground mb-2">
                    {t('form.phone', 'Telefon *')}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+381 60 123 4567"
                    required
                    className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder-text-secondary focus:border-accent focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <label className="block font-body font-medium text-foreground mb-2">
                    {t('form.position', 'Pozicija za koju se prijavljujete *')}
                  </label>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground focus:border-accent focus:outline-none transition-colors"
                  >
                    <option value="">{t('form.selectPosition', 'Izaberite poziciju')}</option>
                    {positions.map(pos => (
                      <option key={pos.value} value={pos.value}>
                        {pos.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-body font-medium text-foreground mb-2">
                  {t('form.message', 'Poruka (opciono)')}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder={t('form.messagePlaceholder', 'Napišite nešto o sebi, vašem iskustvu ili zašto se prijavljujete za ovu poziciju...')}
                  rows={5}
                  className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder-text-secondary focus:border-accent focus:outline-none transition-colors resize-none"
                />
              </div>

              <div>
                <label className="block font-body font-medium text-foreground mb-2">
                  {t('form.cvFile', 'CV fajl (opciono)')}
                </label>
                <div
                  className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-accent transition-colors cursor-pointer"
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                  />
                  
                  {formData.cv ? (
                    <div className="flex items-center justify-center space-x-3">
                      <Icon name="FileText" size={24} className="text-accent" />
                      <div>
                        <p className="font-medium text-foreground">{formData.cv.name}</p>
                        <p className="text-sm text-text-secondary">
                          {(formData.cv.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData(prev => ({ ...prev, cv: null }));
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="text-error hover:text-error/80"
                      >
                        <Icon name="X" size={20} />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Icon name="Upload" size={32} className="text-text-secondary mx-auto mb-3" />
                      <p className="font-medium text-foreground mb-1">
                        {t('form.dragOrClick', 'Prevucite fajl ovde ili kliknite za izbor')}
                      </p>
                      <p className="text-sm text-text-secondary">
                        {t('form.fileTypes', 'PDF, DOC, DOCX (max 5MB)')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-4 bg-accent text-accent-foreground rounded-lg font-body font-medium hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
                    <span>{t('form.sending', 'Šalje se...')}</span>
                  </>
                ) : (
                  <>
                    <Icon name="Send" size={20} />
                    <span>{t('form.submit', 'Pošalji prijavu')}</span>
                  </>
                )}
              </button>
            </form>

          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default JobApplicationForm;

